import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Alert from '@amzn/meridian/alert/alert';
import Column from '@amzn/meridian/column';
import React, { createContext, useCallback, useState } from 'react';
export var ScrollType;
(function (ScrollType) {
    ScrollType[ScrollType["SCROLL_TO_TOP"] = 0] = "SCROLL_TO_TOP";
    ScrollType[ScrollType["DISABLED"] = 1] = "DISABLED";
})(ScrollType || (ScrollType = {}));
export const AlertContext = createContext(null);
export const useAlert = () => {
    const context = React.useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};
/**
 * AlertProvider manages global alert state for the application.
 *
 * Should be placed at the App level to persist across route changes.
 * Automatically clears alerts on navigation to prevent stale alerts.
 * This pairs with the PageAlert component, which renders at the top of page when alert state is non null.
 *
 * Usage:
 * - showAlert(alert) - displays an alert
 * - clearAlert() - manually clears the current alert
 * - alert state is automatically cleared on route changes (there is a key on AlertProvider in App.tsx)
 */
export const AlertProvider = ({ children }) => {
    const [alert, setAlert] = useState(null);
    const clearAlert = useCallback(() => {
        setAlert(null);
    }, [setAlert]);
    const showAlert = useCallback((newAlert) => {
        setAlert(newAlert);
        // Scroll to top if alert is rendered as default behavior unless disabled.
        if (newAlert.scrollType !== ScrollType.DISABLED) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [setAlert]);
    return (_jsx(AlertContext.Provider, { value: { alert, showAlert, clearAlert }, children: _jsxs(Column, { heights: alert ? ['fit', 'fill'] : ['fill'], spacing: "none", children: [alert && (_jsx(Column, { spacingInset: "350 350 none 350", children: _jsx(Alert, { id: "page-alert", "data-testid": "top-level-page-alert", type: alert.type, children: _jsxs(Column, { spacing: "none", children: [alert.title && _jsx("b", { children: alert.title }), _jsx("div", { children: alert.message })] }) }) })), children] }) }));
};
